import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Info, Loader2, Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { PageHeader } from '@/components/efs/PageHeader';
import { MasterTable, type MasterTableColumn } from '@/components/efs/MasterTable';
import { KgValue } from '@/components/efs/KgValue';
import { cn } from '@/lib/utils';
import { formatDateTime } from '@/lib/format';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';

import {
  useComputeWeightDifferenceCheck,
  useConfirmWeightDifferenceCheck,
  useEfConfirmWeightDifferenceCheck,
  useWeightDifferenceChecks,
} from './useWeightDifference';
import type { WeightDifferenceCheckDto, WeightDifferenceSeverity } from './types';

const NS = 'fieldCapture';
const P = 'efs.fieldCapture.weight_difference';

/** critical -> destructive, warning -> amber, record -> muted. */
const severityToneClass: Record<WeightDifferenceSeverity, string> = {
  record: 'bg-efs-bg-subtle text-efs-text-muted',
  warning: 'bg-amber-50 text-amber-900',
  critical: 'bg-destructive text-destructive-foreground',
};

const SeverityBadge = ({ severity }: { severity: WeightDifferenceSeverity }) => {
  const { t } = useTranslation(NS);
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium uppercase tracking-wide',
        severityToneClass[severity],
      )}
    >
      {t(`${P}.severity.${severity}`, { defaultValue: severity })}
    </span>
  );
};

const isSuperseded = (check: WeightDifferenceCheckDto): boolean => check.superseded_at != null;

/** Format a difference_rate fraction (0.0123) as a percentage string. */
const formatRate = (value: string | null): string => {
  if (value == null || value === '') return '-';
  const n = Number.parseFloat(value);
  if (!Number.isFinite(n)) return value;
  return new Intl.NumberFormat('ja-JP', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
  }).format(n);
};

/**
 * Dialog to run a new comparison. Both weights are derived server-side, so this
 * only captures an optional reason — the action just posts that reason.
 */
const RecordCheckDialog = ({
  open,
  onOpenChange,
  onSubmit,
  isPending,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSubmit: (input: { reason: string }) => void;
  isPending: boolean;
}) => {
  const { t } = useTranslation(NS);
  const [reason, setReason] = useState('');

  const reset = () => setReason('');

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(`${P}.record.title`, { defaultValue: '重量差を記録' })}</DialogTitle>
          <DialogDescription>
            {t(`${P}.record.subtitle`, {
              defaultValue:
                '出荷正味と入荷実総重はサーバー側で集計されます。記録すると差分と判定区分を計算します。',
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-efs-text-primary" htmlFor="wd-reason">
              {t(`${P}.fields.reason`, { defaultValue: '理由 / メモ' })}
            </label>
            <Textarea
              id="wd-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t(`${P}.record.reason_placeholder`, {
                defaultValue: '差分の理由を入力（任意）',
              })}
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            {t('efs.common.actions.cancel', { ns: 'common', defaultValue: 'キャンセル' })}
          </Button>
          <Button type="button" onClick={() => onSubmit({ reason })} disabled={isPending}>
            {isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            {t(`${P}.actions.record`, { defaultValue: '記録' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const WeightDifferencePage = () => {
  const { t } = useTranslation(NS);
  const { batchId } = useParams<{ batchId: string }>();
  const user = useCurrentUser();

  const canRecord = hasPermission(user, 'receiving.individual_weight.input');
  const canConfirm = hasPermission(user, 'receiving.weight_discrepancy.confirm_major');
  const canEfConfirm = hasPermission(user, 'estimation.final_decide');

  const [recordOpen, setRecordOpen] = useState(false);

  const query = useWeightDifferenceChecks(batchId);
  const compute = useComputeWeightDifferenceCheck(batchId);
  const confirm = useConfirmWeightDifferenceCheck(batchId);
  const efConfirm = useEfConfirmWeightDifferenceCheck(batchId);

  const checks = useMemo(() => query.data ?? [], [query.data]);

  // A critical check blocks first valuation until BOTH the DK (confirmed_at)
  // and EF (ef_confirmed_at) sign-offs are present.
  const blockingCount = useMemo(
    () =>
      checks.filter(
        (c) =>
          !isSuperseded(c) &&
          c.severity === 'critical' &&
          (c.confirmed_at == null || c.ef_confirmed_at == null),
      ).length,
    [checks],
  );

  const actionError = compute.error ?? confirm.error ?? efConfirm.error ?? null;
  const anyPending = compute.isPending || confirm.isPending || efConfirm.isPending;

  const handleRecord = async ({ reason }: { reason: string }) => {
    await compute.mutateAsync({
      reason: reason.trim() === '' ? null : reason.trim(),
    });
    setRecordOpen(false);
  };

  const columns = useMemo<MasterTableColumn<WeightDifferenceCheckDto>[]>(
    () => [
      {
        key: 'severity',
        header: t(`${P}.fields.severity`, { defaultValue: '判定' }),
        cell: (row) => (
          <div className="flex flex-wrap items-center gap-1.5">
            <SeverityBadge severity={row.severity} />
            {isSuperseded(row) ? (
              <Badge variant="outline" className="border-efs-border text-efs-text-muted">
                {t(`${P}.status.superseded`, { defaultValue: '差替済み' })}
              </Badge>
            ) : null}
          </div>
        ),
      },
      {
        key: 'dispatch_net_weight',
        header: t(`${P}.fields.dispatch_net_weight`, { defaultValue: '出荷正味' }),
        align: 'right',
        cell: (row) => <KgValue value={row.dispatch_net_weight} />,
      },
      {
        key: 'inbound_total_weight',
        header: t(`${P}.fields.inbound_total_weight`, { defaultValue: '入荷実総重' }),
        align: 'right',
        cell: (row) => <KgValue value={row.inbound_total_weight} />,
      },
      {
        key: 'difference_weight',
        header: t(`${P}.fields.difference_weight`, { defaultValue: '差分' }),
        align: 'right',
        cell: (row) => (
          <KgValue
            value={row.difference_weight}
            tone={row.severity === 'critical' ? 'critical' : 'default'}
          />
        ),
      },
      {
        key: 'difference_rate',
        header: t(`${P}.fields.difference_rate`, { defaultValue: '差分率' }),
        align: 'right',
        cell: (row) => (
          <span className="font-mono tabular-nums">{formatRate(row.difference_rate)}</span>
        ),
      },
      {
        key: 'reason',
        header: t(`${P}.fields.reason`, { defaultValue: '理由' }),
        cell: (row) => (
          <div className="max-w-md space-y-1 text-sm text-efs-text-muted">
            <div>{row.reason ?? '-'}</div>
            {isSuperseded(row) ? (
              <div className="rounded-md border border-efs-border bg-efs-bg-subtle px-2 py-1 text-xs">
                {t(`${P}.status.superseded_detail`, {
                  defaultValue: '新しい再計算により履歴扱いになりました。',
                })}
                {row.superseded_at ? ` ${formatDateTime(row.superseded_at)}` : ''}
                {row.superseded_by_check_id
                  ? ` ${t(`${P}.status.replacement`, { defaultValue: '置換先' })}: ${row.superseded_by_check_id.slice(0, 8)}`
                  : ''}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        key: 'confirmed_at',
        header: t(`${P}.fields.confirmed_at`, { defaultValue: 'DK確認' }),
        cell: (row) =>
          row.confirmed_at ? (
            <span className="text-xs text-efs-text-muted">{formatDateTime(row.confirmed_at)}</span>
          ) : isSuperseded(row) ? (
            <span className="text-xs text-efs-text-muted">
              {t(`${P}.status.not_required`, { defaultValue: '不要' })}
            </span>
          ) : (
            <span className="text-xs text-efs-text-muted">
              {t(`${P}.status.unconfirmed`, { defaultValue: '未確認' })}
            </span>
          ),
      },
      {
        key: 'ef_confirmed_at',
        header: t(`${P}.fields.ef_confirmed_at`, { defaultValue: 'EF確認' }),
        cell: (row) =>
          row.ef_confirmed_at ? (
            <span className="text-xs text-efs-text-muted">
              {formatDateTime(row.ef_confirmed_at)}
            </span>
          ) : isSuperseded(row) ? (
            <span className="text-xs text-efs-text-muted">
              {t(`${P}.status.not_required`, { defaultValue: '不要' })}
            </span>
          ) : (
            <span className="text-xs text-efs-text-muted">
              {t(`${P}.status.unconfirmed`, { defaultValue: '未確認' })}
            </span>
          ),
      },
      {
        key: 'actions',
        header: '',
        align: 'right',
        cell: (row) => {
          // A critical check needs two independent confirms (DK + EF); only show
          // each button when the side is still pending and the user is gated for it.
          if (isSuperseded(row)) return null;
          const showDk = canConfirm && row.confirmed_at == null;
          const showEf = row.severity === 'critical' && canEfConfirm && row.ef_confirmed_at == null;
          if (!showDk && !showEf) return null;
          return (
            <div className="flex justify-end gap-2">
              {showDk ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => confirm.mutate(row.id)}
                  disabled={anyPending}
                >
                  {confirm.isPending && confirm.variables === row.id ? (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  ) : null}
                  {t(`${P}.actions.confirm`, { defaultValue: 'DK確認' })}
                </Button>
              ) : null}
              {showEf ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => efConfirm.mutate(row.id)}
                  disabled={anyPending}
                >
                  {efConfirm.isPending && efConfirm.variables === row.id ? (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  ) : null}
                  {t(`${P}.actions.ef_confirm`, { defaultValue: 'EF確認' })}
                </Button>
              ) : null}
            </div>
          );
        },
      },
    ],
    [t, canConfirm, canEfConfirm, anyPending, confirm, efConfirm],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title={t(`${P}.title`, { defaultValue: '重量差チェック' })}
        subtitle={t(`${P}.subtitle`, { defaultValue: '出荷正味と入荷実総重の差分判定' })}
        count={checks.length}
        actions={
          canRecord && batchId ? (
            <Button onClick={() => setRecordOpen(true)} disabled={anyPending}>
              <Plus className="mr-1 h-4 w-4" />
              {t(`${P}.actions.record`, { defaultValue: '重量差を記録' })}
            </Button>
          ) : null
        }
      />

      {actionError ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t(`${P}.error.title`, { defaultValue: '操作できませんでした' })}</AlertTitle>
          <AlertDescription>{actionError.message}</AlertDescription>
        </Alert>
      ) : null}

      {blockingCount > 0 ? (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>
            {t(`${P}.blocking.title`, { defaultValue: '一次査定がブロックされています' })}
          </AlertTitle>
          <AlertDescription>
            {t(`${P}.blocking.description`, {
              count: blockingCount,
              defaultValue:
                '未確認の重大 (critical) 差分が {{count}} 件あります。DK と EF の両方が確認するまで一次査定を作成できません。',
            })}
          </AlertDescription>
        </Alert>
      ) : null}

      <MasterTable
        columns={columns}
        rows={checks}
        rowKey={(row) => row.id}
        rowProps={(row) =>
          isSuperseded(row) ? { className: 'bg-efs-bg-subtle/60 opacity-75' } : undefined
        }
        isLoading={query.isLoading}
        emptyMessage={t(`${P}.empty`, { defaultValue: 'まだ重量差チェックがありません。' })}
      />

      <RecordCheckDialog
        open={recordOpen}
        onOpenChange={setRecordOpen}
        onSubmit={handleRecord}
        isPending={compute.isPending}
      />
    </div>
  );
};

export default WeightDifferencePage;
